import { ApolloClient, InMemoryCache } from '@apollo/client';
import { GetServerSideProps } from 'next';

import { Company } from '@/components/Company';
import { MembershipStatus } from '@/graphql-types/globalTypes';
import { ORGANIZATION } from '@/queries';
import {
  Organization,
  OrganizationVariables,
} from '@/queries/graphql-types/Organization';
import { redirectWithSearchParams } from '@/utilities/redirect-with-search-params';

export const getServerSideProps: GetServerSideProps = async (context) => {
  try {
    const organizationId = context.params?.organizationId.toString();

    if (!organizationId) {
      return {
        notFound: true,
      };
    }

    const client = new ApolloClient({
      ssrMode: true,
      uri: process.env.NEXT_PUBLIC_APOLLO_HTTP_URI,
      cache: new InMemoryCache(),
    });

    const query = await client.query<Organization, OrganizationVariables>({
      query: ORGANIZATION,
      context: {
        headers: {
          tenant_domain: context.req?.headers.tenant_domain,
          authorization: `Bearer ${context.req.cookies['accounts%3AaccessToken']}`, // This is sort of dangerous but it should be fine since we dont have CORS enabled on the next.js server
        },
      },
      fetchPolicy: 'no-cache',
      variables: {
        input: {
          id: organizationId,
        },
      },
    });

    if (!query.data.organization?.result) {
      return {
        notFound: true,
      };
    }

    if (query.data.organization.result.isSponsor) {
      return {
        redirect: {
          destination: redirectWithSearchParams({
            redirectUrl: `/sponsors/${organizationId}/overview`,
            originalUrl: context.req.url,
            omit: ['organizationId'],
          }),
          permanent: false,
        },
      };
    }

    if (
      query.data.organization.result.isMember ||
      query.data.organization.result.membershipData?.membershipStatus ===
        MembershipStatus.MEMBER
    ) {
      return {
        redirect: {
          destination: redirectWithSearchParams({
            redirectUrl: `/members/companies/${organizationId}`,
            originalUrl: context.req.url,
            omit: ['organizationId'],
          }),
          permanent: false,
        },
      };
    }

    return { props: {} };
  } catch (error) {
    if (error?.networkError?.statusCode === 422) {
      return {
        notFound: true,
      };
    }

    throw error;
  }
};

export default Company;
